import React from "react";
import { Check, ChevronRight, RotateCcw } from "lucide-react";
import { heartModuleOverview } from "../data/modules/heart/heartModuleOverview";
import { heartModules } from "../data/modules/heart/heartModules";
import { brainModuleOverview } from "../data/modules/brain/brainModuleOverview";
import { brainModules } from "../data/modules/brain/brainModules";
import { lungsModuleOverview } from "../data/modules/lungs/lungsModuleOverview";
import { lungsModules } from "../data/modules/lungs/lungsModules";
import { colonModuleOverview } from "../data/modules/colon/colonModuleOverview";
import { colonModules } from "../data/modules/colon/colonModules";
import { liverModuleOverview } from "../data/modules/liver/liverModuleOverview";
import { liverModules } from "../data/modules/liver/liverModules";
import { kidneyModuleOverview } from "../data/modules/kidney/kidneyModuleOverview";
import { kidneyModules } from "../data/modules/kidney/kidneyModules";
import { womenreproductiveModuleOverview } from "../data/modules/womenreproductive/womenreproductiveModuleOverview";
import { womenreproductiveModules } from "../data/modules/womenreproductive/womenreproductiveModules";
import { thyroidModuleOverview } from "../data/modules/thyroid/thyroidModuleOverview";
import { thyroidModules } from "../data/modules/thyroid/thyroidModules";
import { skeletonModuleOverview } from "../data/modules/skeleton/skeletonModuleOverview";
import { skeletonModules } from "../data/modules/skeleton/skeletonModules";
import { adrenalglandModuleOverview } from "../data/modules/adrenalglands/adrenalglandModuleOverview";
import { adrenalglandModules } from "../data/modules/adrenalglands/adrenalglandModules";
import { usmle1ModuleOverview } from "../data/modules/usmle1/usmle1ModuleOverview";
import { usmle1Modules } from "../data/modules/usmle1/usmle1Modules";
import { usmle2ModuleOverview } from "../data/modules/usmle2/usmle2ModuleOverview";
import { usmle2Modules } from "../data/modules/usmle2/usmle2Modules";
import { geneticsModuleOverview } from "../data/modules/genetics/geneticsModuleOverview";
import { geneticsModules } from "../data/modules/genetics/geneticsModules";
import { geneticsMethodsModuleOverview } from "../data/modules/geneticsMethods/geneticsMethodsModuleOverview";
import { geneticsMethodsModules } from "../data/modules/geneticsMethods/geneticsMethodsModules";

const OrganOverview = ({ organ, userData, onViewChange, onBack, saveUserData }) => {
  // Get organ-specific modules
  let allModules;
  if (organ === "heart") allModules = heartModuleOverview;
  else if (organ === "brain") allModules = brainModuleOverview;
  else if (organ === "lungs") allModules = lungsModuleOverview;
  else if (organ === "colon") allModules = colonModuleOverview;
  else if (organ === "liver") allModules = liverModuleOverview;
  else if (organ === "kidney") allModules = kidneyModuleOverview;
  else if (organ === "womenreproductive")
    allModules = womenreproductiveModuleOverview;
  else if (organ === "thyroid") allModules = thyroidModuleOverview;
  else if (organ === "skeleton") allModules = skeletonModuleOverview;
  else if (organ === "adrenalgland") allModules = adrenalglandModuleOverview;
  else if (organ === "usmle1") allModules = usmle1ModuleOverview;
  else if (organ === "usmle2") allModules = usmle2ModuleOverview;
  else if (organ === "genetics") allModules = geneticsModuleOverview;
  else if (organ === "genetics-methods") allModules = geneticsMethodsModuleOverview;
  else allModules = []; // Fallback

  let detailedModules;
  if (organ === "heart") detailedModules = heartModules;
  else if (organ === "brain") detailedModules = brainModules;
  else if (organ === "lungs") detailedModules = lungsModules;
  else if (organ === "colon") detailedModules = colonModules;
  else if (organ === "liver") detailedModules = liverModules;
  else if (organ === "kidney") detailedModules = kidneyModules;
  else if (organ === "womenreproductive")
    detailedModules = womenreproductiveModules;
  else if (organ === "thyroid") detailedModules = thyroidModules;
  else if (organ === "skeleton") detailedModules = skeletonModules;
  else if (organ === "adrenalgland") detailedModules = adrenalglandModules;
  else if (organ === "usmle1") detailedModules = usmle1Modules;
  else if (organ === "usmle2") detailedModules = usmle2Modules;
  else if (organ === "genetics") detailedModules = geneticsModules;
  else if (organ === "genetics-methods") detailedModules = geneticsMethodsModules;
  else detailedModules = [];

  const organData = userData.organProgress[organ] || {
    moduleData: {},
    completed: [],
  };
  const geneticsMethodsProgress = userData.organProgress["genetics-methods"] || {
    moduleData: {},
    completed: [],
  };
  const geneticsMethodsCompleted = geneticsMethodsProgress.completed?.length || 0;
  const geneticsMethodsTotal = geneticsMethodsModuleOverview.length;

  const organTitleMap = {
    usmle1: "USMLE 1",
    usmle2: "USMLE 2",
    "genetics-methods": "Genetics Methods",
    womenreproductive: "Women Reproductive",
    adrenalgland: "Adrenal Gland",
  };
  const displayTitle = organTitleMap[organ] || organ;
  const titleSuffix = organTitleMap[organ] ? "" : " System";

  const getModuleStatus = (moduleId) => {
    if (organ === "genetics" && moduleId === "methods") {
      const methodsProgress = userData.organProgress["genetics-methods"] || {
        moduleData: {},
        completed: [],
      };
      const methodsTotal = geneticsMethodsModuleOverview.length;
      const methodsCompleted = methodsProgress.completed?.length || 0;
      if (methodsCompleted === 0) return "not-started";
      if (methodsCompleted >= methodsTotal) return "completed";
      return "review";
    }

    const modProgress =
      organData.moduleData && organData.moduleData[moduleId]
        ? organData.moduleData[moduleId]
        : null;
    if (!modProgress || !modProgress.completed) return "not-started";

    const dueForReview =
      modProgress.nextReviewDate &&
      new Date() >= new Date(modProgress.nextReviewDate);
    if (dueForReview) return "review";
    return "completed";
  };

  const handleStartOver = (e, moduleId, moduleIndex) => {
    e.stopPropagation(); // Prevent other clicks from firing

    const currentOrganProgress = userData.organProgress[organ] || {
      moduleData: {},
      completed: [],
    };
    const currentModData = currentOrganProgress.moduleData || {};
    const existingModuleData = currentModData[moduleId] || {};

    // Reset to section 0 and clear answers
    const newModuleData = {
      ...currentModData,
      [moduleId]: {
        ...existingModuleData,
        currentSection: 0,
        sectionAnswers: {},
        sectionResults: {},
      },
    };

    const newUserData = {
      ...userData,
      organProgress: {
        ...userData.organProgress,
        [organ]: {
          ...currentOrganProgress,
          moduleData: newModuleData,
        },
      },
    };

    saveUserData(newUserData);

    // Navigate to the module with reset flag so OrganModule starts from section 0
    onViewChange(`${organ}-module-${moduleIndex}-reset`);
  };

  const detailedModuleIds = new Set(
    (detailedModules || []).map((module) => module.id),
  );
  const countableModules = allModules.filter((module) =>
    detailedModuleIds.has(module.id),
  );
  const totalModules = countableModules.length;
  const completedModules = countableModules.reduce((count, module) => {
    if (organ === "genetics" && module.id === "methods") {
      return geneticsMethodsCompleted >= geneticsMethodsTotal
        ? count + 1
        : count;
    }
    const modProgress = organData.moduleData?.[module.id];
    const isCompleted =
      modProgress?.completed || organData.completed?.includes(module.id);
    return isCompleted ? count + 1 : count;
  }, 0);
  const progressPercent = (completedModules / totalModules) * 100;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button
        onClick={onBack}
        className="mb-4 text-white hover:text-gray-200 flex items-center gap-2"
      >
        ← Back to Dashboard
      </button>

      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 capitalize mb-2">
              {displayTitle}
              {titleSuffix}
            </h1>
            <p className="text-gray-600">
              Master all modules to become an expert
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-teal-600">
              {completedModules}/{totalModules}
            </div>
            <div className="text-sm text-gray-600">Modules Completed</div>
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-gray-600">Overall Progress</span>
          <span className="font-semibold text-teal-600">
            {Math.round(progressPercent)}%
          </span>
        </div>
        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-teal-400 to-teal-600 transition-all duration-500"
            style={{ width: progressPercent + "%" }}
          />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-white mb-6">
        Available Modules
      </h2>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {allModules.map((module, moduleIndex) => {
        const isGeneticsMethods = organ === "genetics" && module.id === "methods";
        const methodsProgress = isGeneticsMethods
          ? userData.organProgress["genetics-methods"] || {
              moduleData: {},
              completed: [],
            }
          : null;
        const methodsCompletedCount = methodsProgress?.completed?.length || 0;
        const methodsTotalModules = geneticsMethodsModuleOverview.length;
        const status = getModuleStatus(module.id);
        const modProgress =
          organData.moduleData && organData.moduleData[module.id]
            ? organData.moduleData[module.id]
            : null;
        const moduleDetail = detailedModules.find(
          (detail) => detail.id === module.id,
        );
        const totalSections = isGeneticsMethods
          ? methodsTotalModules
          : moduleDetail?.sections?.length || 0;
        const savedSectionIndex = Number.isInteger(modProgress?.currentSection)
          ? modProgress.currentSection
          : -1;
        const answeredCount = modProgress?.sectionAnswers
          ? Object.keys(modProgress.sectionAnswers).length
          : 0;
        const progressCount = isGeneticsMethods
          ? methodsCompletedCount
          : Math.max(savedSectionIndex + 1, answeredCount);
        const moduleProgressPercent =
          totalSections > 0
            ? isGeneticsMethods
              ? Math.min(100, (progressCount / totalSections) * 100)
              : modProgress?.completed
                ? 100
                : Math.min(100, (progressCount / totalSections) * 100)
            : 0;

          return (
            <div
              key={module.id}
              className={
                "rounded-xl p-6 border-2 text-left transition-all transform hover:shadow-xl hover:scale-105 hover:-translate-y-1 bg-gradient-to-br relative overflow-hidden " +
                module.gradient
              }
            >
              {/* Background image - zoomed and shadowed */}
              {module.customImage && (
                <div className="absolute inset-0 flex items-center justify-center opacity-15">
                  <img
                    src={`/${module.customImage}`}
                    alt=""
                    className="w-full h-full object-cover scale-150 blur-sm"
                    style={{ filter: "brightness(0.7)" }}
                  />
                </div>
              )}

              <div className="flex items-start justify-between mb-4 relative z-10">
                {/* Foreground image - clear icon */}
                {module.customImage ? (
                  <div className="bg-white bg-opacity-90 rounded-lg p-3 shadow-md">
                    <img
                      src={`/${module.customImage}`}
                      alt={module.title}
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                ) : (
                  <div className="text-4xl">{module.icon}</div>
                )}

                {status === "completed" && (
                  <span className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-semibold flex items-center gap-1">
                    <Check className="w-3 h-3" /> Completed
                  </span>
                )}
                {status === "review" && (
                  <span className="px-3 py-1 bg-yellow-500 text-white rounded-full text-xs font-semibold">
                    📚 Review
                  </span>
                )}
                {status === "not-started" && (
                  <span className="px-3 py-1 bg-gray-300 text-gray-700 rounded-full text-xs font-semibold">
                    New
                  </span>
                )}
              </div>

              <h3 className="text-xl font-bold text-gray-800 mb-2 relative z-10">
                {module.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4 relative z-10">
                {module.description}
              </p>

              {totalSections > 0 && (
                <div className="mb-4 relative z-10">
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-600">
                    <span>Module Progress</span>
                    <span className="font-semibold text-teal-600">
                      {Math.round(moduleProgressPercent)}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-white/60 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-teal-500 transition-all duration-500"
                      style={{ width: moduleProgressPercent + "%" }}
                    />
                  </div>
                </div>
              )}

              {modProgress && modProgress.completed && (
                <div className="pt-4 border-t border-gray-200 relative z-10">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Completed {modProgress.reviewCount || 1}x</span>
                    {modProgress.nextReviewDate && (
                      <span>
                        Next:{" "}
                        {new Date(
                          modProgress.nextReviewDate
                        ).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between relative z-10">
                <button
                  onClick={() =>
                    isGeneticsMethods
                      ? onViewChange("genetics-methods-overview")
                      : onViewChange(`${organ}-module-${moduleIndex}`)
                  }
                  className="flex items-center gap-2 text-teal-600 font-semibold text-sm hover:text-teal-700 transition-colors"
                >
                  {isGeneticsMethods
                    ? "Open Module"
                    : status === "completed" || status === "review"
                      ? "Review Module"
                      : "Start Module"}
                  <ChevronRight className="w-4 h-4" />
                </button>
                {!isGeneticsMethods && (status === "completed" || status === "review") && (
                  <button
                    onClick={(e) => handleStartOver(e, module.id, moduleIndex)}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-teal-600 transition-colors bg-white/80 hover:bg-white px-3 py-1.5 rounded-lg font-semibold"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Start over
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {completedModules === totalModules && (
        <div className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6 text-center">
          <div className="text-4xl mb-2">🎉</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Congratulations!
          </h3>
          <p className="text-gray-700">
            You've completed all modules for the {organ} system! Keep reviewing
            to maintain mastery.
          </p>
        </div>
      )}
    </div>
  );
};

export default OrganOverview;
